<template>
  <aside class="ops-co" role="dialog" aria-label="Check-out" data-test="ops-checkout-panel">
    <header class="ops-co__head">
      <div class="ops-co__head-text">
        <div class="ops-co__eyebrow">Check-out</div>
        <h2 class="ops-co__name" data-test="ops-checkout-name">{{ patientName }}</h2>
        <div v-if="subLine" class="ops-co__sub" data-test="ops-checkout-sub">{{ subLine }}</div>
      </div>
      <button type="button" class="ops-co__close" title="Close" aria-label="Close" data-test="ops-checkout-close" @click="cancel">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12" /></svg>
      </button>
    </header>

    <PopupErrorDisplay />

    <div class="ops-co__body" data-test="ops-checkout-body">
      <div>
        <div class="ops-co__facts" data-test="ops-checkout-facts">
          <div v-for="f in facts" :key="f.key" class="ops-co__fact" :data-test="`ops-checkout-fact-${f.key}`">
            <div class="ops-co__fact-label">{{ f.label }}</div>
            <div class="ops-co__fact-value" :class="`ops-co__fact-value--${f.tone}`" :data-test="`ops-checkout-fact-value-${f.key}`">{{ f.value }}</div>
            <div v-if="f.detail" class="ops-co__fact-detail">{{ f.detail }}</div>
          </div>
        </div>
        <p class="ops-co__caption">Read only, from Open Dental and Teamio.</p>
      </div>

      <div class="ops-co__section" data-test="ops-checkout-quick">
        <h3 class="ops-co__h">Quick notes</h3>
        <p v-if="quickGroups.length === 0" class="ops-co__caption" data-test="ops-checkout-quick-empty">No quick notes are set up yet. Add them in Practice Ops settings, on the Check-out tab.</p>
        <div v-for="group in quickGroups" :key="group.key" class="ops-co__group" data-test="ops-checkout-group">
          <div class="ops-co__group-title">{{ group.title }}</div>
          <div class="ops-co__chips">
            <button
              v-for="(opt, index) in group.items"
              :key="`${group.key}-${index}`"
              type="button"
              class="ops-co__chip"
              :class="{ 'ops-co__chip--on': opt.checked === true }"
              :aria-pressed="opt.checked === true ? 'true' : 'false'"
              data-test="ops-checkout-chip"
              @click="toggleChip(opt)"
            >{{ opt.name }}</button>
          </div>
        </div>
      </div>

      <div class="ops-co__section">
        <h3 class="ops-co__h"><label for="ops-co-notes">Notes</label></h3>
        <textarea id="ops-co-notes" v-model="notes" rows="4" class="ops-co__area" placeholder="Anything the next visit should know" data-test="ops-checkout-notes"></textarea>
      </div>

      <div class="ops-co__card" data-test="ops-checkout-email">
        <followups-switch :value="sendEmailOn" label="Send post-op follow-up email" :disabled="!isSendEmailEnabled" data-test="ops-checkout-email-switch" @input="setSendEmail" />
        <div class="ops-co__caption">{{ postOpText }}</div>
      </div>
    </div>

    <footer class="ops-co__foot">
      <span class="ops-co__foot-text" data-test="ops-checkout-cancel-text">{{ cancelText }}</span>
      <button type="button" class="ops-co__btn ops-co__btn--ghost" data-test="ops-checkout-cancel" :disabled="saving" @click="cancel">Cancel</button>
      <button type="button" class="ops-co__btn ops-co__btn--primary" data-test="ops-checkout-save" :disabled="saving" @click="submit">{{ saving ? 'Saving…' : 'Save and check out' }}</button>
    </footer>
  </aside>
</template>

<script>
/**
 * Practice Ops Check-out — the redesigned panel (design "Check-out" frame, plan "Design review").
 *
 * `extends` the classic CheckoutNotes.vue, which is the fallback and keeps ALL the load / save logic: init() (the patient's tasks
 * listener + its teardown), _getCheckoutNotes() (the multi-statement result sets, read BY POSITION — never touched), save() (the
 * exact `save-practice-pulse-checkout-notes` payload, then `closePanel({ checkout: true })`) and the computeds the facts read.
 * This file owns only the template and what the new design adds.
 *
 *   • FACTS. Balance and recall come from Schedule View's alert detail for this PatNum (the store's `scheduleView/alertsForDate`),
 *     the same payload the card's icons read; the estimated patient portion, the planned treatment and the open tasks stay as the
 *     classic panel gets them (utils/practiceOps/checkoutFacts.js). The panel's own recall / balance SQL results are not shown.
 *   • QUICK NOTES are chips; a chip adds / removes its auto note (checkoutFacts.applyAutoNote — the classic rule). They are DETACHED
 *     from the board settings first: the classic panel ticks the settings object itself, so a tick used to outlive the panel.
 *   • "Save and check out" is the classic Save. Cancel closes with `{ checkout: false }`, which the page's own flow reads as "not
 *     checked out" and, for a card that was DRAGGED into Check-out, puts it back (cancelCheckout) — the classic Cancel closes with
 *     no payload, which that flow never sees.
 *
 * Opened by PracticeOpsPage.checkoutNotes (same props as classic, plus `cancelNote`), 600 wide, from the right.
 */
import CheckoutNotes from '../CheckoutNotes.vue';
import FollowupsSwitch from '@/components/Followups/FollowupsSwitch.vue';
import { alertsForPatient, describeAlert } from '@/utils/scheduleView/alerts';
import { buildCheckoutFacts, applyAutoNote } from '@/utils/practiceOps/checkoutFacts';

/** A deep copy that shares nothing with the store (quick notes are plain JSON). */
const detach = (value) => JSON.parse(JSON.stringify(value));

export default {
  name: 'OpsCheckoutPanel',

  extends: CheckoutNotes,

  components: { FollowupsSwitch },

  props: {
    /** "Cancel moves <name> back to <Room>." for a card that was dragged here; '' ⇒ "Cancel closes without checking out." */
    cancelNote: { type: String, default: '' },
  },

  data() {
    return { saving: false };
  },

  computed: {
    symbol() {
      const row = this.allSettings && this.allSettings.GENERAL_CURRENCY_SYMBOL;
      return (row && row.PropertyValue) || '$';
    },

    /** Schedule View's alert payload for the practice's today (null until it has loaded, or when the office has no alerts). */
    alertPayload() {
      const getters = this.$store && this.$store.getters;
      return (getters && getters['scheduleView/alertsForDate']) || null;
    },

    facts() {
      return buildCheckoutFacts({
        payload: this.alertPayload,
        patNum: this.patientNum,
        oop: this.patientOutofPocket,
        planned: this.patientPlannedAppts,
        tasks: this.tasksTaggedWithPatient,
        symbol: this.symbol,
      }, { alertsForPatient, describe: describeAlert });
    },

    /** "Dr. Kapoor · Crown prep" — who and what the visit was. */
    subLine() {
      const item = this.patientItem || {};
      const provider = this.provider ? `${this.provider.FName || ''} ${this.provider.LName || ''}`.trim() : '';
      const proc = typeof item.procDescript === 'string' ? item.procDescript.trim() : '';
      return [provider, proc].filter(Boolean).join(' · ');
    },

    /** The quick-note groups, whichever shape was saved ({ key: { title, val: [] } }, or the old { Title: [] }). */
    quickGroups() {
      const notes = this.quickNotes;
      if (!notes || typeof notes !== 'object') return [];
      return Object.keys(notes).map((key) => {
        const value = notes[key];
        if (Array.isArray(value)) return { key, title: key, items: value };
        return { key, title: (value && value.title) || key, items: value && Array.isArray(value.val) ? value.val : [] };
      }).filter((g) => g.items.length > 0);
    },

    sendEmailOn() {
      return this.sendEmail === true;
    },

    postOpText() {
      if (!this.isSendEmailEnabled) return 'Set the post-op send time in Practice Ops settings, on the Check-out tab, to turn this on.';
      const time = this.practicePulseSettings.sendEmailTime;
      const who = this.provider ? `Dr. ${`${this.provider.FName || ''} ${this.provider.LName || ''}`.trim()}` : 'the provider';
      return this.sendEmailOn
        ? `Adds this visit to the follow-up call list emailed to ${who} at ${time}.`
        : 'This visit is not added to the follow-up call list.';
    },

    cancelText() {
      return this.cancelNote || 'Cancel closes without checking out.';
    },
  },

  methods: {
    /** The classic load, then detach the quick notes from the board settings (see the header). */
    async _getCheckoutNotes() {
      await CheckoutNotes.methods._getCheckoutNotes.call(this);
      const settingsNotes = this.practicePulseSettings && this.practicePulseSettings.quickNotesOptions2;
      const fromSettings = !!settingsNotes && this.quickNotes === settingsNotes;
      if (this.quickNotes && typeof this.quickNotes === 'object') {
        const copy = detach(this.quickNotes);
        if (fromSettings) {
          // a fresh visit: nothing is ticked, whatever an earlier panel left on the settings object
          Object.keys(copy).forEach((key) => {
            const items = Array.isArray(copy[key]) ? copy[key] : (copy[key] && copy[key].val) || [];
            items.forEach((it) => { if (it && typeof it === 'object') it.checked = false; });
          });
        }
        this.quickNotes = copy;
      }
    },

    toggleChip(opt) {
      const next = opt.checked !== true;
      this.$set(opt, 'checked', next);
      this.notes = applyAutoNote(this.notes, opt.autoNotes, next);
    },

    setSendEmail(on) {
      this.sendEmail = on ? true : 'FALSE';
    },

    /** Save and check out: the classic save(), guarded against a double click. */
    async submit() {
      if (this.saving) return;
      this.saving = true;
      try {
        await this.save();
      } finally {
        this.saving = false;
      }
    },

    /** Cancel / the X: `{ checkout: false }` (see the header). */
    cancel() {
      this.setErrored2(false);
      this.setMessage2('');
      this.closeSlideOutPanel({ checkout: false });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../components/Followups/mixins';

.ops-co {
  @include fu-font-base;
  display: flex;
  flex-direction: column;
  // Exactly the slide-out's height, with the BODY as the scroller, so Cancel / Save stay pinned (as Task settings does).
  height: 100%;
  min-height: 0;
  background: $fu-surface;
}

.ops-co__head {
  flex: none;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 18px 16px 16px 24px;
  border-bottom: 1px solid $fu-border;
}

.ops-co__head-text { flex: 1; min-width: 0; }

.ops-co__eyebrow {
  @include fu-eyebrow($fu-brand);
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.ops-co__name {
  @include fu-font-display;
  margin: 3px 0 0;
  font-size: 23px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: $fu-text;
}

.ops-co__sub {
  margin-top: 2px;
  font-size: 13px;
  color: $fu-text-muted;
}

.ops-co__close { @include fu-icon-btn(40px); }

.ops-co__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 20px 24px;
}

.ops-co__facts {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
}

.ops-co__fact {
  min-width: 0;
  padding: 11px 13px;
  background: $fu-neutral-50;
  border: 1px solid $fu-border;
  border-radius: 10px;
}

.ops-co__fact-label { font-size: 12px; color: $fu-text-muted; }

.ops-co__fact-value {
  margin-top: 2px;
  font-size: 14.5px;
  font-weight: $fu-fw-bold;
  color: $fu-text;
  overflow-wrap: anywhere;
}

.ops-co__fact-value--bad { color: #c2333a; }
.ops-co__fact-value--muted { color: $fu-text-muted; font-weight: $fu-fw-semibold; }

.ops-co__fact-detail {
  margin-top: 2px;
  font-size: 12px;
  color: $fu-text-muted;
  overflow-wrap: anywhere;
}

.ops-co__caption {
  margin: 6px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: $fu-text-muted;
}

.ops-co__section { display: flex; flex-direction: column; gap: 10px; }

.ops-co__h {
  margin: 0;
  font-size: 15px;
  font-weight: 800;
  color: $fu-text;
  label { margin: 0; font: inherit; }
}

.ops-co__group-title {
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: $fu-fw-bold;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: $fu-text-muted;
}

.ops-co__chips { display: flex; flex-wrap: wrap; gap: 6px; }

.ops-co__chip {
  @include fu-button-reset;
  @include fu-focus-ring;
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 13px;
  border: 1px solid $fu-border-strong;
  border-radius: 999px;
  background: $fu-surface;
  color: $fu-text;
  font-size: 13px;
  font-weight: $fu-fw-semibold;
  &:hover { background: $fu-neutral-50; }
}

.ops-co__chip--on {
  border-color: $fu-brand;
  background: $fu-brand-50;
  color: $fu-brand-700;
}

.ops-co__area {
  @include fu-input(auto);
  min-height: 92px;
  padding: 10px 12px;
  resize: vertical;
  font-size: 14px;
  line-height: 1.5;
}

.ops-co__card {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px;
  border: 1px solid $fu-border;
  border-radius: 12px;
  .ops-co__caption { margin: 0; padding-left: 2px; }
}

.ops-co__foot {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 24px;
  border-top: 1px solid $fu-border;
  background: $fu-surface;
}

.ops-co__foot-text {
  flex: 1;
  min-width: 0;
  font-size: 12.5px;
  color: $fu-text-muted;
}

.ops-co__btn { @include fu-btn(secondary, sm); flex: none; }
.ops-co__btn--ghost { @include fu-btn(ghost, sm); }
.ops-co__btn--primary { @include fu-btn(primary, sm); }

@include fu-phone {
  .ops-co__head { padding: 14px 12px 12px 16px; }
  .ops-co__body { padding: 16px; }
  .ops-co__facts { grid-template-columns: minmax(0, 1fr); }
  .ops-co__foot { flex-wrap: wrap; padding: 12px 16px; }
  .ops-co__foot-text { flex: 1 0 100%; }
}
</style>

<style lang="scss">
// The design's lighter backdrop behind THIS panel only (as Task settings does). The page opens it with cssClass 'ops-co-slideout'.
.slideout-panel:has(.ops-co-slideout) .slideout-panel-bg {
  background-color: rgba(12, 15, 14, 0.18);
}
</style>
